"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.apiUrl = apiUrl;
exports.loadScript = loadScript;
exports.assetUrl = assetUrl;
exports.setCookie = setCookie;
exports.getCookie = getCookie;
exports.setLoadingButton = setLoadingButton;
exports.templateReplace = templateReplace;
exports.sendEvent = sendEvent;
exports.sendGEvent = sendGEvent;
exports.agentCredit = agentCredit;
exports.promoCode = promoCode;
exports.sendScrollAdjustment = sendScrollAdjustment;
exports.sendHeightAdjustment = sendHeightAdjustment;
exports.sendRedirect = sendRedirect;
exports.zipToState = zipToState;
exports.regularOrderFormUrl = regularOrderFormUrl;
exports.navigateAway = navigateAway;
/**
 * A URL on this server, under whatever public path this page was published at.
 *
 * `/order2/api/ugps` on hub.wire3.com, `/fiber/api/ugps` behind the wire3.com reverse proxy.
 * The server strips the prefix again before routing (see the /order2 block in server.ts), so
 * one nginx location forwards the whole form and never names an API path.
 *
 * W3_BASE comes from order-form2.ejs. The default keeps a stale cached page working.
 */
function apiUrl(path) {
    const base = window.W3_BASE || '/order2';
    return `${base}${path}`;
}
/**
 * Inject a third-party <script> on demand, and resolve once it has run.
 *
 * A vendor only the Details step needs (Chargebee, pdf.js) does not belong in the head: the
 * address gate is the first screen, and it downloads them for a visitor who never reaches
 * payment (SEO handoff 2026-09-17, §5). Call this from the step that actually needs one.
 *
 * De-duped by URL, so every caller can just await it. A failed load drops the cached promise,
 * so a later retry (the customer reopens the step) gets a fresh <script>.
 */
const LOADED_SCRIPTS = {};
function loadScript(src, attrs = {}) {
    const cached = LOADED_SCRIPTS[src];
    if (cached) {
        return cached;
    }
    const pending = new Promise((resolve, reject) => {
        const el = document.createElement('script');
        el.async = true;
        for (const name of Object.keys(attrs)) {
            el.setAttribute(name, attrs[name]);
        }
        el.onload = () => resolve();
        el.onerror = () => {
            delete LOADED_SCRIPTS[src];
            reject(new Error(`[OF] failed to load ${src}`));
        };
        // src last: setting it before the handlers can race a cached response
        el.src = src;
        document.head.append(el);
    });
    LOADED_SCRIPTS[src] = pending;
    return pending;
}
/**
 * A static asset on this server, under the same public path prefix as apiUrl().
 *
 * The EJS templates write `<%= BASE %>/img/...`; markup the bundle injects has no EJS,
 * so it must prefix the same way (see FlavorOverrides EERO).
 */
function assetUrl(path) {
    return apiUrl(path);
}
/**
 * Set a cookie.
 * @param {string} name - The name of the cookie.
 * @param {string} val - The value of the cookie.
 * @param {number} expDays - The number of days until the cookie expires (default is 30 days).
 */
function setCookie(name, val, expDays = 30) {
    // setup cookie expiration
    const date = new Date();
    date.setTime(date.getTime() + (expDays * 24 * 60 * 60 * 1000));
    // set the cookie
    document.cookie = `${name}=${val};expires=${date.toUTCString()};path=/`;
}
/**
 * Get a cookie by name.
 * @param {string} name - The name of the cookie to retrieve.
 * @return {string} The value of the cookie, or an empty string if not found.
 */
function getCookie(name) {
    let decoded = decodeURIComponent(document.cookie).split(';');
    for (let i = 0, len = decoded.length; i < len; i++) {
        let cookie = decoded[i].trim();
        if (cookie.indexOf(name + '=') == 0) {
            return cookie.substring(name.length + 1, cookie.length);
        }
    }
    return "";
}
/**
 * Set a button into a loading state.
 * Requires the button to have a child <span> with the class `lb-og-text` and an <svg> with class `lb-og-icon`.
 * @param {HTMLButtonElement} btnEl - The button element to modify.
 * @param {boolean} isLoading - Whether to set or unset the loading state.
 * @param {string} [newText] - Optional new text to display while loading (default is 'Loading...').
 */
function setLoadingButton(btnEl, isLoading, newText) {
    if (!newText) {
        newText = 'Loading...';
    }
    let children = btnEl.children;
    let originalText = btnEl.querySelector('.lb-og-text'), originalIcon = btnEl.querySelector('.lb-og-icon');
    let loadingText = btnEl.querySelector('.lb-load-text'), spinner = btnEl.querySelector('.lb-load-icon');
    if (isLoading) {
        btnEl.disabled = true;
        originalText.insertAdjacentHTML('afterend', `<span class="lb-load-text">${newText}</span>`);
        originalText.classList.add('d-none');
        btnEl.insertAdjacentHTML('beforeend', `<span class="spinner lb-load-icon" role="status" aria-hidden="true"></span>`);
        originalIcon.classList.add('d-none');
    }
    else {
        btnEl.disabled = false;
        if (loadingText) {
            loadingText.remove();
        }
        originalText.classList.remove('d-none');
        if (spinner) {
            spinner.remove();
        }
        originalIcon.classList.remove('d-none');
    }
}
function templateReplace(html, key, val) {
    return html.replaceAll(`\${${key}}`, val);
}
function sendEvent(eventName, state, data) {
    const commonData = getCommonData(state);
    try {
        let res = fetch(apiUrl(`/api/caEvent`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                profileUuid: commonData.uuid,
                data: {
                    // v4: per-event idempotency key + client timestamp + attribution envelope
                    eventId: crypto?.randomUUID ? crypto.randomUUID() : undefined,
                    clientTs: new Date().toISOString(),
                    stepName: eventName,
                    data: data,
                    formInput: commonData.formInput,
                    ugpsData: commonData.ugpsData,
                    userSelection: commonData.userSelection,
                    attribution: commonData.attribution,
                    url: window.location.href,
                    isInDev: state.config.devMode,
                    isInIframe: state.config.inIframe,
                    abMode: commonData.abMode ?? undefined,
                    events_version: 4
                },
            })
        });
    }
    catch (ignored) {
        console.log(`[OrderForm Events] Unable to send event: ${eventName}`, state, data);
    }
}
function sendGEvent(eventName, state, data) {
    // if (!state.config.inIframe) return;
    if (state.config.devMode)
        return;
    const commonData = getCommonData(state);
    switch (eventName) {
        case 'generate_lead':
            // interest only ?
            break;
        case 'disqualify_lead':
            data = {
                disqualified_lead_reason: state?.ugpsData?.code?.toUpperCase() ?? 'UNKNOWN'
            };
            break;
        case 'view_item_list':
            data['canInstall'] = state?.ugpsData?.canInstall ?? false;
            data['item_list_id'] = 'offers';
            data['item_list_name'] = 'Offers';
            break;
        case 'select_item':
            data['item_list_id'] = 'offers';
            data['item_list_name'] = 'Offers';
            break;
        case 'select_promotion':
            data['promotion_id'] = 'offers';
            data['promotion_name'] = 'Offers';
            break;
        case 'view_cart':
            data = getGEventSelectedItems(state);
            break;
        case 'confirmedContact':
            data = getGEventSelectedItems(state);
            break;
        case 'purchase':
            data = {
                ...getGEventSelectedItems(state),
                transaction_id: state.cookies.uuid,
                coupon: state.userInput.selection?.plan?.discountTitle || undefined
            };
            break;
        case 'add_payment_info':
            data = {
                ...getGEventSelectedItems(state),
                transaction_id: state.cookies.uuid,
                coupon: state.userInput.selection?.plan?.discountTitle || undefined,
                payment_type: 'Credit Card',
                cid: data.cid,
                canSchedule: data.canSchedule
            };
            break;
    }
    // GA4 reads the money off the event itself: `value` + `currency` from getGEventSelectedItems().
    // NO formInput here. It holds name, email, phone and address, and GA4 rejects PII. uuid,
    // abMode and agent are the safe join keys back to the order.
    const gaParams = {
        ...data,
        uuid: commonData.uuid,
        abMode: commonData.abMode,
        agent: commonData.formInput.agent || undefined
    };
    const gaSent = sendEmbedGtagEvent(eventName, gaParams);
    window.parent.postMessage(JSON.stringify({
        anm: true,
        stepCompleted: eventName,
        eventData: {
            ...commonData,
            ...data
        },
        // The ready-made GA4 payload for a host-side dataLayer.push. Push THIS, never `eventData`
        // above — eventData carries formInput, and formInput is PII.
        gaEvent: { name: eventName, params: gaParams },
        // true when this iframe already fired the event into its own GTM container. The host must
        // skip its push when this is true, or GA4 counts every purchase twice.
        gaSent: gaSent,
        // get around gtag's PII redaction, this isn't PII...
        agent: commonData.formInput.agent || null
    }), 'https://wire3.com/*');
}
/**
 * Fire the event into the GTM container this iframe loads itself (views/order-form2.ejs).
 * Keep identical to order-form-new/src/Utils.ts sendEmbedGtagEvent().
 *
 * Apr 2025 (a9510f2) replaced the gtag() call with the postMessage above, and the host page never
 * pushed the payload to its dataLayer. Purchase revenue stopped reaching GA4 that day. This is the
 * same gtag() call the form made before that commit.
 *
 * gtm.js does not always define window.gtag before the form runs, so define the shim the form used
 * back then: gtag() is a dataLayer push of its own arguments object.
 *
 * TEMPORARY. The view sets window.__w3EmbedGtm next to the GTM snippet. When GTM moves to the host
 * page only, delete both lines from the view. This call then goes quiet, `gaSent` turns false, and
 * the host handler takes over with no change here.
 */
function sendEmbedGtagEvent(eventName, params) {
    const w = window;
    if (!w.__w3EmbedGtm)
        return false;
    try {
        w.dataLayer = w.dataLayer || [];
        if (typeof w.gtag !== 'function') {
            w.gtag = function () { w.dataLayer.push(arguments); };
        }
        w.gtag('event', eventName, params);
        return true;
    }
    catch (ignored) {
        console.log(`[OrderForm Events] gtag failed for: ${eventName}`, params);
        return false;
    }
}
/**
 * The rep credit to send with an order. THIS LANDING'S URL WINS OVER THE COOKIE.
 * Keep identical to order-form-new/src/Utils.ts agentCredit() — same contract, same reason.
 *
 * `attribution.params` is the union captureAttribution() builds with this landing spread last, so
 * `params.agent` is the `?agent=` on the page the customer is on right now. The `agent` cookie only
 * changes when a server `Set-Cookie` is accepted, so it drops updates and goes stale. Aug 2026: a
 * cookie left over from another rep's link beat the URL of the rep's own page, twice, and paid the
 * wrong person. wire3-portal docs/ORDER_ATTRIBUTION_MISCREDIT_MWOODRUFF_SEP2026.md.
 */
function agentCredit(state) {
    return state.attribution?.params?.['agent'] || state.cookies.agent || '';
}
/**
 * The promo/coupon code to price the offers with. THIS LANDING'S URL WINS, BUT THE STORE STILL
 * COUNTS — same shape as agentCredit() above, and for the same reason.
 *
 * The gate's promo box prefills from this, and a code the customer types there wins over it (see
 * YourInfoStep.promoValue). `?promo=` on the iframe src is not enough on its own: the [order_form]
 * shortcode only puts that param on the src when THIS landing carried it, so a customer who picked
 * the promo up on an earlier wire3.com page and then reached a cached /availability/ arrived with
 * no `?promo=` — and the code never reached /api/ugps, so the offers that came back were never
 * priced with it.
 *
 * `attribution.params` is the union captureAttribution() builds with this landing spread last: it
 * holds the current `?promo=` when there is one, and the promo carried in the shared `.wire3.com`
 * store cookie otherwise. The raw param stays as the fallback for a browser that persisted nothing.
 */
function promoCode(state) {
    return state.attribution?.params?.['promo'] || state.config.urlParams.get('promo') || '';
}
function getCommonData(state) {
    return {
        uuid: state.cookies.uuid,
        abMode: !!state.config.additionalParameters['abMode'] ? 'B' : 'A',
        formInput: {
            ...state.userInput.profile,
            // every marketing param this visitor carries — the attribution UNION, so a tag that
            // arrived via the shared .wire3.com cookie shows up here too, not just this URL's query
            // string (spec §6.4)
            ...state.attribution.params,
            // DELIBERATELY the cookie first, which is the OPPOSITE of agentCredit() above. This field
            // is the forensic witness, not the credit: `formInput.agent` (cookie) next to
            // `attribution.params.agent` (URL) is the only signature that shows a stale cookie, and it
            // is what wire3-portal's zz_probe_stale_cookie_census.ts keys on.
            agent: state.cookies.agent || state.attribution.params['agent'] || '',
            inCustomerHub: state.config.customerHub
        },
        ugpsData: {
            ...state.ugpsData,
            offers: undefined,
            flavor: undefined
        },
        userSelection: {
            plan: state?.userInput?.selection?.plan?.title,
            addons: state?.userInput?.selection?.addons?.map((a) => a.title) || []
        },
        // v4: full attribution envelope rides on every event
        attribution: state.attribution
    };
}
function getGEventSelectedItems(state) {
    let selectedPlan = state.userInput.selection.plan;
    let selectedAddons = state.userInput.selection?.addons || [];
    let selectedPlanIndex = state.ugpsData.offers?.findIndex(o => o.title === selectedPlan.title);
    return {
        currency: 'USD',
        value: selectedPlan.monthlyPrice + selectedAddons.reduce((a, c) => a + c.price, 0),
        items: [{
                item_id: selectedPlan.title,
                item_name: selectedPlan.detailedTitle ?? selectedPlan.title,
                price: selectedPlan.monthlyPrice,
                discount: selectedPlan.discountAmount,
                ...(Number(selectedPlan.discountAmount > 0) ? { coupon: selectedPlan.discountTitle } : {}),
                index: selectedPlanIndex,
                item_category: 'Plan',
                item_brand: 'Wire 3',
                location_id: state.userInput.profile?.address
            }].concat(selectedAddons.map(a => {
            return {
                item_id: a.title,
                item_name: a?.flavor?.title ?? a.title,
                price: a.price,
                type: a.type,
                item_category: (a.type?.toUpperCase() === 'PROMOTION') ? 'Promotion' : 'Addon',
                item_brand: 'Wire 3',
                location_id: state.userInput.profile?.address
            };
        }))
    };
}
function sendScrollAdjustment(px) {
    window.parent.postMessage(JSON.stringify({
        scroll: px || 0
    }), '*');
}
let previousHeight = 0;
function sendHeightAdjustment(px) {
    if (previousHeight !== px) {
        previousHeight = px || 0;
        window.parent.postMessage(JSON.stringify({
            'height': px
        }), '*');
    }
}
function sendRedirect(url) {
    window.parent.postMessage(JSON.stringify({
        redirect: url
    }), '*');
}
function zipToState(zip) {
    return false;
}
/**
 * The regular order form — where a COMMERCIAL address gets handed off to.
 *
 * /order2 is residential-only, so a business address leaves for the form that does sell to one
 * (see YourInfoStep.submitProfile). This is the wire3.com page that iframes /order, NOT the hub's
 * own /order: the customer is on wire3.com and should stay there rather than land on a bare form
 * on another host. Change this one line to move the hand-off.
 */
const REGULAR_ORDER_FORM_URL = 'https://wire3.com/availability';
/**
 * REGULAR_ORDER_FORM_URL, carrying everything the customer has typed so far.
 *
 * The prefill survives the hop into the iframe because the [order_form] shortcode forwards each
 * of these names from its own query string into the iframe src (wordpress/functions.php
 * URL_SOURCES), and /order renders them through PASS_THRU. `phone` keeps the masked
 * "(555) 555-5555" shape — that is exactly what the regular form's phone input validates against.
 */
function regularOrderFormUrl(state) {
    const profile = state.userInput.profile;
    const params = new URLSearchParams();
    const add = (key, value) => { if (value) {
        params.set(key, value);
    } };
    add('address', profile?.address);
    add('firstName', profile?.firstName);
    add('lastName', profile?.lastName);
    add('email', profile?.email);
    add('phone', profile?.phoneNumber);
    add('promo', profile?.promo);
    // rep credit rides along explicitly: /order re-reads ?agent= and refreshes the cookie from it,
    // so the hop cannot resurrect a stale cookie the way it did in Aug 2026 — see agentCredit()
    add('agent', agentCredit(state));
    const query = params.toString();
    return `${REGULAR_ORDER_FORM_URL}${query ? `?${query}` : ''}`;
}
/**
 * Leave the order form for `url`.
 *
 * Top level (wire3.com/fiber, hub.wire3.com/order2) navigates itself. Embedded, only the parent
 * can move — order-form-parent-handler.js turns a `redirect` message into window.location.href,
 * the same contract the thank-you redirect uses.
 */
function navigateAway(url) {
    if (window.self !== window.top) {
        sendRedirect(url);
        return;
    }
    window.location.href = url;
}
